/**
 * Parse an expiry string (a message payload's `expiresAt`, a campaign's
 * `schedule_config.endDate`) into the Date the DynamoDB TTL writers take. Both
 * sources are unvalidated JSON, and an unparseable one would reach
 * `toInAppTtlSeconds` as NaN, which the DynamoDB marshaller rejects outright
 * ("Special numeric value NaN is not allowed") — failing the whole write rather
 * than just the TTL. Returning undefined instead writes the item with no TTL,
 * which is the safe fallback.
 *
 * This lives outside dynamo-queries.ts on purpose: callers auto-mock that
 * module, and a stubbed parser would silently drop every TTL under test.
 */
export function parseInAppExpiresAt(
  expiresAt: string | null | undefined,
): Date | undefined {
  if (!expiresAt) {
    return undefined;
  }
  const parsed = new Date(expiresAt);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

export const INAPP_EXPIRATION_UNITS = [
  "minute",
  "hour",
  "day",
  "week",
] as const;

export type InAppExpirationUnit = (typeof INAPP_EXPIRATION_UNITS)[number];

export type InAppMessageExpiration = {
  quantity: number;
  unit: InAppExpirationUnit;
};

const EXPIRATION_UNIT_MS: Record<InAppExpirationUnit, number> = {
  minute: 60_000,
  hour: 3_600_000,
  day: 86_400_000,
  week: 604_800_000,
};

// Undefined when unset ("never") or malformed, so the item falls back to the campaign's end.
export function parseInAppMessageExpirationMs(
  raw: unknown,
): number | undefined {
  if (typeof raw !== "object" || raw === null) return undefined;
  const { quantity, unit } = raw as { quantity?: unknown; unit?: unknown };
  if (typeof quantity !== "number" || !Number.isFinite(quantity)) {
    return undefined;
  }
  if (quantity <= 0 || typeof unit !== "string") return undefined;
  const unitMs = Object.hasOwn(EXPIRATION_UNIT_MS, unit)
    ? EXPIRATION_UNIT_MS[unit]
    : undefined;
  return unitMs === undefined ? undefined : quantity * unitMs;
}

export function inAppItemExpiresAt({
  receivedAt,
  expirationMs,
  campaignEndDate,
}: {
  receivedAt: Date;
  expirationMs: number | undefined;
  campaignEndDate: Date | undefined;
}): Date | undefined {
  const own =
    expirationMs === undefined
      ? undefined
      : new Date(receivedAt.getTime() + expirationMs);
  if (!own) return campaignEndDate;
  if (!campaignEndDate) return own;
  return own < campaignEndDate ? own : campaignEndDate;
}
